import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { DeviceTimePatternCard } from '../../../components/deviceTimePattern/DeviceTimePatternCard';
import { DeviceTimePatternDetails } from '../../../components/deviceTimePattern/DeviceTimePatternDetails';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { toast } from "react-toastify";
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { Button } from '../../../components/button/Button';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { DeviceTimePattern as DeviceTimePatternModel } from "../../../models/DeviceTimePattern";
import { TabSummary } from '../../../models/TabSummary';
import { assignDeviceTimePattern, getDeviceTimePatternById, getDeviceTimePatternNamesWithPagination, storeDeviceTimePattern } from '../../../services/deviceTimePatternService';
import { calculateDuration, getTime, setTime } from '../../../services/utilities/dateFormatting';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from "../../../store/types";
import { hideColumns } from '../../../models/utilities/columnHelper';
import TimeTriggeredActionColumn from '../../../models/ColumnDefinitions/TimeTriggeredActionColumn';
import { getTimeTriggeredActionNameWithPagination, getTimeTriggeredActions } from '../../../services/timeTriggeredActionService';
import { appendFilter } from '../../../utilities/filters';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import SiteSearch from '../../../models/search/SiteSearch';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import DeviceTimePatternSearch from '../../../models/search/DeviceTimePatternSearch';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import TimeTriggeredActionSearch from '../../../models/search/TimeTriggeredActionSearch';
import AssignedTab from '@/components/assignWizard/AssignedTab';
import AssignModalTwo from '@/components/assignWizard/AssignModal';
import { PaginationModel } from '@/models/Pagination';
import { ListQuery } from '@/models/ListQuery';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem: DeviceTimePatternModel = {
    days: '',
    id: 0,
    startTime: '',
    endTime: '',
    site: '',
    name: '',
    duration: '',
    dayList: new Array(0),
    default: undefined,
    siteId: undefined,
    startDate: undefined,
    endDate: undefined,
    endTimeExtract: '',
    originalName: ''
}

const allItemsPath = '/access-control/hardware/device-time-patterns';
const itemPath = '/access-control/hardware/device-time-pattern/';

const DeviceTimePattern: FC = () => {
    const { translate } = useLocale();
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.timeTriggeredAction), badge: 0, showInitial: true, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<DeviceTimePatternModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const dialog = useDialog();
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const lookup: Lookup[] = [
        {
            name: 'devAction.deviceAction.deviceImprox.site.id',
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.timePattern,
            values: [],
            dataMethod: getDeviceTimePatternNamesWithPagination,
            searchAccessor: DeviceTimePatternSearch.find((item) => item.accessor === "name")?.searchOnName,
        },
    ];

    const getAssignedTimeTriggeredAction = (listParameters?: ListQuery) => {
        return getTimeTriggeredActions({
            ...listParameters,
            filters: appendFilter(
                itemId,
                "Time Pattern",
                "timePattern.id",
                listParameters?.filters ?? []
            ),
        });
    };

    const topic: Topic = {
        accessor: 'tpName',
        title: translate.byKey(TranslationKeys.deviceTimePatterns),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const getTabContent = (index?: number) => {
        const selectedTabContent = tabContentList[index ?? 0];
        return selectedTabContent.content;
    };

    const tabContentList: ContentWrapper[] = [{
        content: <DeviceTimePatternDetails />
    },
    {
        content: <AssignedTab
            key={`AssignTimeTriggeredActionFor${itemId}`}
            column={TimeTriggeredActionColumn}
            searchColumn={TimeTriggeredActionSearch}
            id={itemId}
            dataMethod={getAssignedTimeTriggeredAction}
            title={translate.byKey(TranslationKeys.assignedTimeTriggeredActions)}
            lookup={lookup}>
            <Button variant="submit" shape="rounded" className="ml-8 py-1 px-5" onClick={() => {setShowAddModal(true);}}>{translate.byKey(TranslationKeys.manage)}</Button>
        </AssignedTab>
    },]

    const reloadPage = async () => {
        try {
            setTabs([]);
            let deviceTimePattern: any;

            if (itemId) {
                deviceTimePattern = await getDeviceTimePatternById(itemId, translate);

                if (deviceTimePattern.startTime) {
                    deviceTimePattern.startTime = setTime(deviceTimePattern.startTime);
                    deviceTimePattern.endTime = setTime(deviceTimePattern.endTime);
                }

                setSelectedItem({label: deviceTimePattern.name, value: deviceTimePattern.id.toString(), isDefault: deviceTimePattern.isDefault})
                setCurrentItem(deviceTimePattern);
                const updatedTabs = updateTabBadgeTotal(1, deviceTimePattern?.timeTriggeredActions, initialTabs);
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.deviceTimePatterns) },
                { value: itemPath + `${itemId}`, displayName: `${deviceTimePattern?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<string[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>, setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = [];
            do {
                const timeTriggeredActionsForTimePattern = await getTimeTriggeredActionNameWithPagination({ filters: itemId ? appendFilter(itemId, 'Time Pattern', 'timePattern.id', []) : [], pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(timeTriggeredActionsForTimePattern.rows.map(row => row.value));
                rows = rows.concat(timeTriggeredActionsForTimePattern.rows.map(row => ({ id: row.value, name: row.label })));
                pagination = timeTriggeredActionsForTimePattern.pagination;
                pageNumber++;
            } while(pageNumber <= pagination.totalPages);

            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(ids);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusyLoader(false);
        }
    }

    const saveDeviceTimePattern = (values) => {
        const deviceTimePattern: DeviceTimePatternModel = {
            ...currentItem,
            name: values.name,
            siteId: itemId ? values.siteId : siteId,
            id: +itemId,
            startTime: values.startTime ? getTime(values.startTime) : "0",
            duration: values.startTime && values.endTime ? calculateDuration(values.startTime, values.endTime) : "2400",
            endTimeExtract: values.endTime ? getTime(values.endTime) : "0",
            startDate: values.startDate,
            endDate: values.endDate,
            dayList: values.dayList
        }

        const isNew = itemId ? +itemId < 0 : true;
        const storeMethod = storeDeviceTimePattern;
        saveItem(setBusy, storeMethod, deviceTimePattern, isNew, setItemId, history);
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const assignTimeTriggeredActions = (toBeAdded: string[]) => {
        setBusy(true);
        assignDeviceTimePattern(toBeAdded, itemId)
            .then(async () => {
                toast.success(translate.byKey(TranslationKeys.success));
                reloadPage();
            }).catch((err) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err) });
            })
            .finally(() => {
                setBusy(false);
            });
    }
    const saveItem = (setBusy, storeMethod, item, createNewItem, setItemId, history) => {
        setBusy(true);
        storeMethod(item, createNewItem)
            .then(async (value) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                reloadPage();
                toast.success(translate.byKey(TranslationKeys.deviceTimePatternSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <div className='flex h-full'>
        <TopicContextProvider topic={topic} dataMethod={getDeviceTimePatternNamesWithPagination} itemId={itemId} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveDeviceTimePattern}
                    tabs={tabs}
                    initialValues={currentItem}
                    itemPath={itemPath}
                    children={
                        <DeviceTimePatternCard />} />
            </ItemContentTabTwo>
        </TopicContextProvider>
        <AssignModalTwo
            isOpen={showAddModal}
            dataMethod={(listParameters) => getTimeTriggeredActions({ ...listParameters, filters: appendFilter(currentItem?.siteId, 'Site', 'timePattern.site.id', listParameters?.filters ?? []) })}
            lookup={lookup}
            onClose={() => { setShowAddModal(false) }}
            modalTitle={translate.byKey(TranslationKeys.assignTimeTriggeredActions)}
            assigningTitle={translate.byKey(TranslationKeys.timeTriggeredActions)}
            tableColumns={hideColumns(TimeTriggeredActionColumn, ['timePattern'])}
            searchColumns={TimeTriggeredActionSearch}
            getAssignedItems={getAssignedItems}
            handleSubmit={assignTimeTriggeredActions} />
    </div>
}

export default DeviceTimePattern;
